/**
 * HcoGrid.tsx — Organizations V3 (HCO)
 * AG Grid Community wrapper for HCO organization search results.
 * Columns: Status, Org Name (with address sub-line), Preview, Org Type,
 * City, State, Affiliations, then CTMS / CRM / Spend per the hco.json role/team rules.
 * Uses AG Grid 36 "alpine" theme — matches ExUsProfessionalsV3/HcpV3 grids.
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry, themeAlpine } from 'ag-grid-community';
import styles from '../OrganizationsV3.module.css';
import type { HcoSearchRowV3 } from '../../../api/organizationsV3Api';
import Toggle from '../../../globalcomponents/Toggle';
import {
    rowKey, toggleState, visibleFlagColumns,
    type FlagKind, type FlagOverrides, type FlagUser, type ToggleState,
} from '../flagRules';

// Register all Community modules once
ModuleRegistry.registerModules([AllCommunityModule]);

// ---------------------------------------------------------------------------
// Cell renderers
// ---------------------------------------------------------------------------

function StatusCellRenderer({ value }: ICellRendererParams) {
    const isActive = !value || value === 'A' || value === 'ACTIVE';
    return (
        <div className={styles.statusCell} title={value ?? 'Active'}>
            {isActive
                ? <span style={{ color: '#27ae60', fontSize: 18 }}>✔</span>
                : <span style={{ color: '#aaa', fontSize: 18 }}>○</span>
            }
        </div>
    );
}

interface OrgNameCellParams extends ICellRendererParams {
    data: HcoSearchRowV3;
    onViewDetails: (row: HcoSearchRowV3) => void;
}

function OrgNameCellRenderer({ data, onViewDetails }: OrgNameCellParams) {
    if (!data) return null;
    const orgName     = (data.orgName ?? '—').toUpperCase();
    const addressParts = [data.address1, data.city, data.state, data.zip5].filter(Boolean);
    const addressLine  = addressParts.join(', ');

    return (
        <div className={styles.nameCell}>
            <button
                className={styles.namePrimary}
                style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
                onClick={() => onViewDetails(data)}
                data-testid="name-link"
            >
                {orgName}
            </button>
            {addressLine && (
                <div className={styles.nameSecondary}>{addressLine}</div>
            )}
        </div>
    );
}

function PreviewCellRenderer({ data, onPreview }: { data: HcoSearchRowV3; onPreview: (row: HcoSearchRowV3) => void } & ICellRendererParams) {
    return (
        <button
            className={styles.previewBtn}
            onClick={() => onPreview(data)}
            title="Preview"
            data-testid="btn-preview"
        >
            👁
        </button>
    );
}

interface FlagContext {
    user?:     FlagUser;
    overrides: FlagOverrides;
    errorKey:  string | null;
    onToggle:  (row: HcoSearchRowV3, kind: FlagKind, state: ToggleState) => void;
}

function FlagCellRenderer(params: ICellRendererParams<HcoSearchRowV3> & { kind: FlagKind }) {
    const { data, kind } = params;
    const ctx = params.context as FlagContext;
    if (!data) return null;
    const state = toggleState(kind, data, ctx.user, ctx.overrides);
    const onClick = () => ctx.onToggle(data, kind, state);
    return (
        <div className={styles.flagCell} data-testid={`toggle-${kind}`}>
            <Toggle
                active={state.active}
                disabled={state.disabled}
                global={state.global}
                error={ctx.errorKey === `${rowKey(data)}|${kind}`}
                click={onClick}
                errorCallback={onClick}
            />
        </div>
    );
}

function AffiliationCellRenderer({ value }: ICellRendererParams) {
    if (value === null || value === undefined) return <span style={{ color: '#aaa' }}>—</span>;
    return <span>{value}</span>;
}

// ---------------------------------------------------------------------------
// Grid component
// ---------------------------------------------------------------------------

interface Props {
    rows:          HcoSearchRowV3[];
    onPreview:     (row: HcoSearchRowV3) => void;
    onViewDetails: (row: HcoSearchRowV3) => void;
    user?:         FlagUser;
    overrides?:    FlagOverrides;
    errorKey?:     string | null;
    onToggle?:     (row: HcoSearchRowV3, kind: FlagKind, state: ToggleState) => void;
}

const noopToggle = () => {};

function buildFlagColumns(user: FlagUser | undefined): ColDef<HcoSearchRowV3>[] {
    return visibleFlagColumns(user).map((kind): ColDef<HcoSearchRowV3> => ({
        colId:              `flag-${kind}`,
        headerName:         kind,
        valueGetter:        (p) => p.data ? toggleState(kind, p.data, user, (p.context as FlagContext).overrides).active : false,
        width:              80,
        sortable:           true,
        cellRenderer:       FlagCellRenderer,
        cellRendererParams: { kind },
        cellStyle:          { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    }));
}

export default function HcoGrid({
    rows, onPreview, onViewDetails, user, overrides = {}, errorKey = null, onToggle = noopToggle,
}: Props) {
    const gridRef = useRef<AgGridReact<HcoSearchRowV3>>(null);
    const context = useMemo((): FlagContext => ({ user, overrides, errorKey, onToggle }),
        [user, overrides, errorKey, onToggle]);

    // Cell renderers read toggle state from `context`; AG Grid doesn't re-render on a new
    // context, so refresh just the flag cells (rebuilding columnDefs would reset widths).
    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true, columns: ['flag-CTMS', 'flag-CRM', 'flag-Spend'] });
    }, [context]);
    const orgNameRenderer = useCallback((params: ICellRendererParams) => (
        <OrgNameCellRenderer {...params} onViewDetails={onViewDetails} />
    ), [onViewDetails]);

    const previewRenderer = useCallback((params: ICellRendererParams) => (
        <PreviewCellRenderer {...params} onPreview={onPreview} />
    ), [onPreview]);

    const columnDefs = useMemo((): ColDef<HcoSearchRowV3>[] => [
        {
            headerName:   'Status',
            field:        'orgStatus',
            width:        70,
            sortable:     true,
            cellRenderer: StatusCellRenderer,
            headerClass:  'ag-header-center',
            cellStyle:    { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
            headerName:   'Org Name',
            field:        'orgName',
            flex:         3,
            minWidth:     300,
            sortable:     true,
            cellRenderer: orgNameRenderer,
            comparator:   (a, b) => (a ?? '').localeCompare(b ?? ''),
        },
        {
            headerName:   'Preview',
            field:        'orgId',
            width:        80,
            sortable:     false,
            cellRenderer: previewRenderer,
            cellStyle:    { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
            headerName:   'Org Type',
            field:        'orgType',
            width:        150,
            sortable:     true,
        },
        {
            headerName:   'City',
            field:        'city',
            width:        130,
            sortable:     true,
        },
        {
            headerName:   'State',
            field:        'state',
            width:        60,
            sortable:     true,
        },
        {
            headerName:   'Affiliations',
            field:        'affiliationCount',
            width:        110,
            sortable:     true,
            cellRenderer: AffiliationCellRenderer,
            cellStyle:    { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        ...buildFlagColumns(user),
    ], [orgNameRenderer, previewRenderer, user]);

    const defaultColDef = useMemo((): ColDef => ({
        resizable: true,
        sortable:  true,
    }), []);

    return (
        <div
            className={styles.gridWrapper}
            style={{ width: '100%' }}
            data-testid="hco-grid"
        >
            <AgGridReact<HcoSearchRowV3>
                ref={gridRef}
                context={context}
                theme={themeAlpine}
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowHeight={48}
                headerHeight={38}
                domLayout="autoHeight"
                suppressPaginationPanel={true}
                animateRows={true}
                getRowId={(params) => String(params.data.orgId ?? Math.random())}
            />
        </div>
    );
}
